import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  ChatCircleDots, X, PaperPlaneRight, Robot, CalendarBlank, Phone, Spinner,
} from "@phosphor-icons/react";

import { useBooking } from "@/contexts/BookingContext";
import { trackLeadConversion } from "@/lib/analytics";
import { onChatEvent } from "@/lib/chatBus";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_REPLIES = [
  "My PC is slow",
  "I have a virus",
  "Wi-Fi keeps dropping",
  "Business IT pricing",
];

const PROACTIVE_SEEN_KEY = "tc_chat_proactive_seen";

function sessionId() {
  let id = sessionStorage.getItem("tc_chat_session");
  if (!id) {
    id = `chat_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem("tc_chat_session", id);
  }
  return id;
}

function Bubble({ role, text }) {
  const isBot = role !== "user";
  return (
    <div
      data-testid={`chat-msg-${role}`}
      className={`flex ${isBot ? "justify-start" : "justify-end"}`}
    >
      <div
        className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-[14px] leading-relaxed whitespace-pre-wrap ${
          isBot
            ? "bg-zinc-100 text-zinc-900 rounded-bl-md"
            : "bg-blue-600 text-white rounded-br-md"
        }`}
      >
        {text}
      </div>
    </div>
  );
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [proactive, setProactive] = useState(null); // { text, openerForChat } | null
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Hi! I'm Carolina, TechConnect's virtual assistant. How can I help today — slow PC, virus, Wi-Fi, or something for your business?",
    },
  ]);
  const { openBooking } = useBooking();
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [messages, open]);

  // Proactive nudge listener (fired by ServicePage after 20s of dwelling)
  useEffect(() => {
    const unsub = onChatEvent((evt) => {
      if (evt?.type !== "proactive") return;
      if (open) return; // don't interrupt an open chat
      if (sessionStorage.getItem(PROACTIVE_SEEN_KEY) === "1") return;
      setProactive({
        text: evt.text || "Got a question? I can help right now.",
        openerForChat: evt.openerForChat || null,
      });
      sessionStorage.setItem(PROACTIVE_SEEN_KEY, "1");
    });
    return unsub;
  }, [open]);

  const dismissProactive = () => setProactive(null);

  const openFromProactive = () => {
    const p = proactive;
    setProactive(null);
    if (p?.openerForChat) {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: p.openerForChat },
      ]);
    }
    setOpen(true);
  };

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: trimmed }]);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/chat/message`, {
        sessionId: sessionId(),
        message: trimmed,
      });
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: data.reply,
          suggestBooking: data.suggestBooking,
          leadCaptured: data.leadCaptured,
        },
      ]);
      if (data.leadCaptured) {
        trackLeadConversion({ service: "AI Chat", event_source: "chat-widget" });
      }
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text:
            "Sorry — I hit a snag. For immediate help, call (919) 737-5442 or use the booking button.",
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Proactive teaser bubble */}
      {proactive && !open && (
        <div
          data-testid="chat-proactive-teaser"
          className="fixed bottom-44 left-6 z-[60] w-[min(92vw,320px)] bg-white rounded-2xl shadow-2xl border border-zinc-200 p-4 pr-9 animate-in fade-in slide-in-from-bottom-3 duration-300"
        >
          <button
            onClick={dismissProactive}
            data-testid="chat-proactive-dismiss"
            aria-label="Dismiss"
            className="absolute top-2 right-2 h-7 w-7 rounded-full text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 grid place-items-center"
          >
            <X size={14} weight="bold" />
          </button>
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-full bg-blue-600 text-white grid place-items-center shrink-0">
              <Robot size={18} weight="duotone" />
            </div>
            <div>
              <div className="text-xs font-semibold text-blue-600">Carolina · TechConnect</div>
              <div className="text-sm text-zinc-800 leading-snug mt-1">{proactive.text}</div>
              <button
                onClick={openFromProactive}
                data-testid="chat-proactive-open"
                className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 text-xs font-semibold transition-colors"
              >
                Reply <ChatCircleDots size={12} weight="bold" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating launcher */}
      <button
        data-testid="chat-launcher"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close chat" : "Open chat"}
        className={`fixed bottom-24 left-6 z-[60] h-14 w-14 rounded-full shadow-xl shadow-blue-600/40 transition-all duration-300 grid place-items-center text-white ${
          open
            ? "bg-zinc-900 hover:bg-zinc-800 rotate-90"
            : "bg-blue-600 hover:bg-blue-700 hover:scale-110"
        }`}
      >
        {open ? (
          <X size={22} weight="bold" />
        ) : (
          <ChatCircleDots size={26} weight="fill" />
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-44 left-6 z-[60] w-[min(96vw,400px)] h-[min(80vh,600px)] bg-white rounded-2xl shadow-2xl border border-zinc-200 flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200"
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-white/15 grid place-items-center">
              <Robot size={20} weight="duotone" />
            </div>
            <div className="flex-1">
              <div className="font-semibold leading-tight">Carolina · TechConnect</div>
              <div className="text-xs text-blue-100 flex items-center gap-1.5 mt-0.5">
                <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                Online · replies in seconds
              </div>
            </div>
            <button
              onClick={() => openBooking("")}
              data-testid="chat-book-btn"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-white/15 hover:bg-white/25 px-3 py-1.5 text-xs font-semibold transition-colors"
            >
              <CalendarBlank size={14} weight="bold" />
              Book
            </button>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              data-testid="chat-close"
              className="h-8 w-8 rounded-full hover:bg-white/15 grid place-items-center"
            >
              <X size={18} weight="bold" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={listRef}
            data-testid="chat-messages"
            className="flex-1 overflow-y-auto p-4 space-y-3 bg-zinc-50"
          >
            {messages.map((m, i) => (
              <React.Fragment key={i}>
                <Bubble role={m.role} text={m.text} />
                {m.suggestBooking && (
                  <div className="flex justify-start">
                    <button
                      onClick={() => openBooking("AI chat lead")}
                      data-testid={`chat-action-book-${i}`}
                      className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-semibold shadow-md shadow-blue-600/20 transition-all"
                    >
                      <CalendarBlank size={16} weight="bold" />
                      Open booking calendar
                    </button>
                  </div>
                )}
                {m.leadCaptured && (
                  <div className="flex justify-start">
                    <div
                      data-testid="chat-lead-captured"
                      className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-1.5 text-xs font-semibold"
                    >
                      Lead captured · we&apos;ll call within 1 business hour
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-zinc-100 rounded-2xl rounded-bl-md px-4 py-3 inline-flex items-center gap-2 text-zinc-500">
                  <Spinner size={14} className="animate-spin" />
                  <span className="text-xs">Carolina is typing…</span>
                </div>
              </div>
            )}
          </div>

          {/* Quick replies (only if conversation just started) */}
          {messages.length <= 1 && !busy && (
            <div className="px-4 pb-2 flex flex-wrap gap-2 bg-zinc-50">
              {QUICK_REPLIES.map((q) => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  data-testid={`chat-quick-${q.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-xs rounded-full border border-blue-200 bg-white text-blue-700 hover:bg-blue-50 px-3 py-1.5 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="border-t border-zinc-200 bg-white p-3 flex items-center gap-2"
          >
            <a
              href="tel:+19197375442"
              data-testid="chat-call-btn"
              title="Call now"
              className="h-10 w-10 rounded-full bg-zinc-100 hover:bg-zinc-200 grid place-items-center text-zinc-700 shrink-0"
            >
              <Phone size={18} weight="bold" />
            </a>
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your question…"
              disabled={busy}
              className="flex-1 rounded-full border border-zinc-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="submit"
              data-testid="chat-send"
              disabled={busy || !input.trim()}
              className="h-10 w-10 rounded-full bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-300 text-white grid place-items-center transition-all shrink-0"
            >
              <PaperPlaneRight size={18} weight="bold" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
